import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { getReconDataForCase } from '@/lib/utils/quality';
import { uniqBy, filter } from 'lodash';
import { augmentRuleData } from '@/lib/utils/ruleGrid';
import { computeDiagnosisData } from '@/lib/utils/episodesAndTreatmentSets';
import { clearError } from './errorSlice';
import { handleApiError } from '@/utils/apiErrorHandler';
import formatDate from '@/lib/utils/date-time/utils';

const normalizeQueryValue = (value?: string): string | undefined => {
  if (!value) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeRepeatedly = (value?: string): string | undefined => {
  if (!value) return value;
  let normalized = value;
  for (let i = 0; i < 3; i += 1) {
    try {
      const decoded = decodeURIComponent(normalized);
      if (decoded === normalized) break;
      normalized = decoded;
    } catch {
      break;
    }
  }
  return normalized;
};

export const fetchMeasuresConditions = createAsyncThunk(
  'quality/fetchMeasuresConditions',
  async ({
    mpin,
    size,
    sort,
    population,
    compliant,
    patientID,
    isDelegate
  }: {
    mpin: string,
    size: number,
    sort: string,
    population: string,
    compliant: string,
    patientID: string,
    isDelegate: boolean
  }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/measure-conditions?size=${size}` : `/papi/secure/quality/measure-conditions?size=${size}`;
    if (population) url = `${url}&condition=${population}`;
    if (compliant) url = `${url}&compliant=${compliant}`;
    if (patientID) url = `${url}&patientID=${patientID}`;
    if (sort) url = `${url}&sort=${sort}`;

    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching conditions from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  }
);

export const fetchMeasuresPopulations = createAsyncThunk(
  'quality/fetchMeasuresPopulations',
  async ({ mpin, condition, compliant, patientID, size, isDelegate }: {
    mpin: string;
    size: number;
    condition?: string;
    compliant?: string;
    patientID?: string;
    isDelegate: boolean
  }, { dispatch }) => {
    let routeUrl = isDelegate ? `/papi/secure/physician/${mpin}/quality/measure-populations` : `/papi/secure/quality/measure-populations?size=${size}`;
    const addQuery = (url: string, key: string, value?: string) =>
      value ? `${url}${url.includes('?') ? '&' : '?'}${key}=${value}` : url;

    routeUrl = addQuery(routeUrl, 'condition', condition);
    routeUrl = addQuery(routeUrl, 'compliant', compliant);
    routeUrl = addQuery(routeUrl, 'patientID', patientID);
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching populations from:', routeUrl);
      const response = await axios.get(routeUrl);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  }
);

export const fetchQualityMeasuresDetails = createAsyncThunk(
  'quality/fetchQualityMeasuresDetails',
  async ({
    mpin,
    page,
    size,
    sort,
    section,
    reconCaseID,
    reconQualityPatientID,
    reconQualityCondition,
    condition,
    compliant,
    patientID,
    population,
    stepIndicator,
    isDelegate,
    isCaseClosed,
    changeRequestApplied
  }: {
    mpin: string;
    page: number;
    size: number;
    sort: string,
    section?: string,
    reconCaseID?: string,
    reconQualityPatientID?: string,
    reconQualityCondition?: string,
    condition?: string,
    compliant?: string,
    patientID?: string,
    population?: string,
    stepIndicator?: number,
    isDelegate: boolean,
    isCaseClosed: boolean
    changeRequestApplied?: string
  }, { dispatch }) => {
    const baseUrl = isCaseClosed
      ? (isDelegate ? `/papi/secure/physician/${mpin}/case/measures` : `/papi/secure/case/measures`)
      : (isDelegate ? `/papi/secure/physician/${mpin}/quality/measure-detail` : `/papi/secure/quality/measure-detail`);

    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort,
    });

    if (section) params.set('section', section);
    if (reconCaseID) params.set('reconCaseID', reconCaseID);
    if (reconQualityPatientID) params.set('reconQualityPatientID', reconQualityPatientID);
    if (reconQualityCondition) params.set('reconQualityCondition', normalizeRepeatedly(reconQualityCondition) || '');
    if (condition) params.set('condition', condition);
    if (compliant) params.set('compliant', compliant);
    if (patientID) params.set('patientID', patientID);
    if (population) params.set('population', population);
    if (stepIndicator) params.set('stepIndicator', '1');
    if (changeRequestApplied) params.set('changeRequestApplied', changeRequestApplied);

    const url = `${baseUrl}?${params.toString()}`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching measure details from:', url);
      const response = await axios.get(url);
      return { ...response.data, type: size === 5000 ? 'dropdown' : 'table' };
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const fetchQualityMeasuresPatients = createAsyncThunk(
  'quality/fetchQualityMeasuresPatients',
  async ({
    mpin,
    size,
    section,
    reconCaseID,
    reconQualityPatientID,
    reconQualityCondition,
    compliant,
    condition,
    population,
    sort,
    isDelegate
  }: {
    mpin: string;
    size: number;
    section?: string,
    reconCaseID?: string,
    reconQualityPatientID?: string,
    reconQualityCondition?: string,
    compliant?: string,
    condition?: string,
    population?: string,
    sort?: string,
    isDelegate: boolean
  }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/patient-detail?size=${size}` : `/papi/secure/quality/patient-detail?size=${size}`;
    if (section) url = `${url}&section=${section}`;
    if (reconCaseID) url = `${url}&reconCaseID=${reconCaseID}`;
    if (reconQualityPatientID) url = `${url}&reconQualityPatientID=${reconQualityPatientID}`;
    if (reconQualityCondition) url = `${url}&reconQualityCondition=${normalizeQueryValue(reconQualityCondition)}`;
    if (compliant) url = `${url}&compliant=${compliant}`
    if (condition) url = `${url}&condition=${condition}`
    if (population) url = `${url}&population=${population}`
    if (sort) sort = `&sort=${sort}`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching patient details from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const fetchMeasuresCompliance = createAsyncThunk(
  'quality/ fetchMeasuresCompliance',
  async ({ mpin, measureID, isDelegate }: { mpin: string; measureID: string, isDelegate: boolean }, { dispatch }) => {
    const url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/compliance?measureID=${measureID}` : `/papi/secure/quality/recon/compliance?measureID=${measureID}`;
    try {
      dispatch(clearError());
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching designation overview from:', url);
      const response = await axios.get(url);

      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const fetchMeasuresExclusion = createAsyncThunk
  ('quality/fetchMeasuresExclusion', async ({ mpin, measureID, isDelegate }: { mpin: string; measureID: string, isDelegate: boolean }, { dispatch }) => {
    const url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion?measureID=${measureID}` : `/papi/secure/quality/recon/exclusion?measureID=${measureID}`;
    try {
      dispatch(clearError());
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching designation overview from:', url);
      const response = await axios.get(url);

      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const fetchMeasuresComplianceRules = createAsyncThunk(
  'quality/fetchMeasuresComplianceRules',
  async ({ mpin, measureID, reconCaseID, caseStatus, isDelegate = false, requestID }:
    { mpin: string; measureID: string; reconCaseID?: string; caseStatus: string, isDelegate: boolean, requestID?: string },
    { dispatch }) => {
    let routeUrl = !isDelegate ? `/papi/secure/quality/recon/compliance/rules?measureID=${measureID}&caseStatus=${caseStatus}` :
      `/papi/secure/physician/${mpin}/quality/recon/compliance/rules?measureID=${measureID}&caseStatus=${caseStatus}`;
    if (reconCaseID) routeUrl += `&reconCaseID=${reconCaseID}`
    if (requestID) routeUrl += `&requestID=${requestID}`
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching designation overview from:', routeUrl);
      const response = await axios.get(routeUrl);

      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  }
);

export const measuresComplianceRulesPost = createAsyncThunk('quality/measuresComplianceRulesPost',
  async ({ mpin, payload, update, isDelegate }: { mpin: string, payload: any, update: boolean, isDelegate: boolean }, { dispatch }) => {
    let url = !isDelegate ? `/papi/secure/quality/recon/compliance/rules` :
      `/papi/secure/physician/${mpin}/quality/recon/compliance/rules`;
    if (update) {
      url += `?update=${update}`;
    }

    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl Fetching designation overview from:', url);
      const response = await axios.post(url, payload);

      return response.status;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const measuresComplianceRulesDelete = createAsyncThunk('quality/measuresComplianceRulesDelete', async (
  { mpin, measureID, patientID, conditionID, EBMPEGFlag, isDelegate }:
    { mpin: string; patientID: string, measureID: string, conditionID: string, EBMPEGFlag: string, isDelegate: boolean }, { dispatch }) => {
  const url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/compliance`
    : `/papi/secure/quality/recon/compliance`

  try {
    dispatch(clearError());
    console.log('routeUrl Measures Compliance Delete from:', url);
    const response = await axios.post(url, { measureID, patientID, conditionID, EBMPEGFlag });

    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const fetchMeasureExclusionRules = createAsyncThunk('quality/ fetchMeasureExclusionRules', async (
  { mpin, measureID, reconCaseID, caseStatus, isDelegate, requestID }: { mpin: string; measureID: string, reconCaseID: string, caseStatus: string, isDelegate: boolean, requestID?: string }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion/rules?measureID=${measureID}&caseStatus = ${caseStatus}` : `/papi/secure/quality/recon/exclusion/rules?measureID=${measureID}&caseStatus = ${caseStatus}`;
  if (reconCaseID) url += `&reconCaseID=${reconCaseID}`;
  if (requestID) url += `&requestID=${requestID}`;
  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl Fetching patient details from:', url);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const measuresExclusionRulesPost = createAsyncThunk('quality/measuresComplianceRulesPost', async ({ mpin, payload, update, isDelegate }: { mpin: string, payload: any, update: boolean, isDelegate: boolean }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion/rules` : `/papi/secure/quality/recon/exclusion/rules`;
  if (update) {
    url += `?update=${update}`;
  }
  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl Fetching patient details from:', url);
    const response = await axios.post(url, payload);
    return response.status;
  } catch (error) {
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const measuresExclusionRulesDelete = createAsyncThunk('quality/measuresExclusionRulesDelete', async ({
  mpin, measureID, patientID, conditionID, EBMPEGFlag, isDelegate }:
  { mpin: string; measureID: string, patientID: string, conditionID: string, EBMPEGFlag: string, isDelegate: boolean }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion`
    : `/papi/secure/quality/recon/exclusion`;
  try {
    dispatch(clearError());
    console.log('routeUrl Measures Exclusion Delete from:', url);
    const response = await axios.post(url, { measureID, patientID, conditionID, EBMPEGFlag });
    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const fetchMeasureAttribution = createAsyncThunk('quality/fetchMeasureAttribution', async ({
  mpin, size, sort, direction, page, measureID, EBMPEGFlag, isDelegate }:
  {
    mpin: string;
    measureID: string,
    size: number,
    sort: string,
    direction: string,
    page: number,
    EBMPEGFlag: string,
    isDelegate: boolean
  }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/attribution/services?size=${size}&sort=${sort},${direction}&page=${page}&measureID=${measureID}&EBMPEGFlag=${EBMPEGFlag}`
    : `/papi/secure/quality/recon/attribution/services?size=${size}&sort=${sort},${direction}&page=${page}&measureID=${measureID}&EBMPEGFlag=${EBMPEGFlag}`;
  try {
    dispatch(clearError());
    console.log('routeUrl Fetching attribution details from:', url);
    const response = await axios.get(url);
    return { data: response?.data?.data, totalItems: response?.data?.totalItems };
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const measureAttributionPost = createAsyncThunk('quality/measureAttributionPost', async ({ mpin, payload, update, isDelegate }: { mpin: string; payload: any, update: boolean, isDelegate: boolean }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/attribution` : `/papi/secure/quality/recon/attribution`;
  if (update) {
    url += `?update=${update}`;
  }
  try {
    dispatch(clearError());
    console.log('routeUrl Fetching attribution details from:', url);
    const response = await axios.post(url, payload);
    return response.status;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const measureAttributionDelete = createAsyncThunk('quality/measureAttributionDelete', async (
  { mpin, measureID, patientID, EBMPEGFlag, isDelegate }:
    { mpin: string; measureID: string; patientID: string; EBMPEGFlag: string; isDelegate: boolean }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/attribution/remove`
    : `/papi/secure/quality/recon/attribution/remove`;
  try {
    dispatch(clearError());
    console.log('routeUrl measure attribution delete from:', url);
    const response = await axios.post(url, { measureID, patientID, EBMPEGFlag });
    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const complianceQualityAttachmentPost = createAsyncThunk('quality/complianceQualityAttachmentPost',
  async ({ mpin, payload, isDelegate }: { mpin: string, payload: any, isDelegate: boolean }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/compliance/attachment`
      : `/papi/secure/quality/recon/compliance/attachment`;
    try {
      dispatch(clearError());
      console.log('routeUrl measure attribution post from:', url);
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const complianceQualityAttachmentDelete = createAsyncThunk('quality/complianceQualityAttachmentDelete',
  async ({ mpin, measureID, patientID, EBMPEGFlag, isDelegate }: { mpin: string; measureID: string, patientID: string, EBMPEGFlag: string, isDelegate: boolean }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/compliance/attachment/remove`
      : `/papi/secure/quality/recon/compliance/attachment/remove`;
    try {
      dispatch(clearError());
      console.log('routeUrl measure compliance attachment delete from:', url);
      const response = await axios.post(url, { measureID, patientID, EBMPEGFlag });
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const exclusionQualityAttachmentPost = createAsyncThunk('quality/exclusionQualityAttachmentPost',
  async ({ mpin, payload, isDelegate }: { mpin: string, payload: any, isDelegate: boolean }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion/attachment`
      : `/papi/secure/quality/recon/exclusion/attachment`;
    try {
      dispatch(clearError());
      console.log('routeUrl measure attribution delete from:', url);
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      //  pendingRequests.delete(requestId);
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  });

export const exclusionQualityAttachmentDelete = createAsyncThunk('quality/exclusionQualityAttachmentDelete', async ({ mpin, measureID, patientID, EBMPEGFlag, isDelegate }: { mpin: string; measureID: string, patientID: string, EBMPEGFlag: string, isDelegate: boolean }, { dispatch }) => {
  let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/recon/exclusion/attachment/remove`
    : `/papi/secure/quality/recon/exclusion/attachment/remove`;
  try {
    dispatch(clearError());
    console.log('routeUrl measure exclusion attachment delete from:', url);
    const response = await axios.post(url, { measureID, patientID, EBMPEGFlag });
    return response.data;
  } catch (error) {
    //  pendingRequests.delete(requestId);
    handleApiError(error as AxiosError, "quality", dispatch);
    throw error;
  }
});

export const fetchServiceDetails = createAsyncThunk(
  'quality/fetchServiceDetails',
  async ({ mpin, isDelegate, measureID }:
    { mpin: string; isDelegate: boolean, measureID: string }, { dispatch }) => {
    let url = isDelegate ? `/papi/secure/physician/${mpin}/quality/service-detail?measureID=${measureID}`
      : `/papi/secure/quality/service-detail?measureID=${measureID}`;
    try {
      dispatch(clearError());
      console.log('routeUrl measure attribution delete from:', url);
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "quality", dispatch);
      throw error;
    }
  })

// Create slice
const qualitySlice = createSlice({
  name: 'quality',
  initialState: {
    data: { measures: [] as Array<{ measureID: string; reconFlag?: boolean }> },
    loading: false,
    error: '',
    totalItems: 0,
    populations: [],
    patients: [],
    conditions: [],
    complianceDetails: {
      loading: false,
      totalItems: 0,
      compliance: [],
    },
    complianceRules: { rules: {}, loading: false },
    exclusionDetails: {
      loading: false,
      totalItems: 0,
      exclusions: [] as Array<{ reconCaseSystemID: string; attributionExclusionDescription?: string }>,
    },
    exclusionRules: { rules: {}, loading: false },
    attributionDetails: {
      loading: false,
      totalItems: 0,
      attributions: [],
      description: ''
    },
    activeRecon: { type: '' },
    reconModalData: {},
    serviceDetail: [] as Array<{ date: string }>
  },
  reducers: {
    setReconModalData: (state, action) => {
      state.reconModalData = action.payload;
    },
    setQualityDetails: (state, action) => {
      const { measureID, hasRecon } = action.payload;
      state.data.measures = state.data.measures?.map((item: { measureID: string; reconFlag?: boolean }) =>
        item.measureID === measureID
          ? { ...item, reconFlag: hasRecon }
          : item
      );
    },
    clearQualityMeasuresInfo: (state) => {
      state.data = { measures: [] };
      state.loading = false;
      state.error = '';
      state.totalItems = 0;
      state.populations = [];
      state.patients = [];
      state.conditions = [];
      state.complianceDetails = {
        loading: false,
        totalItems: 0,
        compliance: [],
      };
      state.complianceRules = { rules: {}, loading: false };
      state.exclusionDetails = {
        loading: false,
        totalItems: 0,
        exclusions: [] as Array<{ reconCaseSystemID: string; attributionExclusionDescription?: string }>,
      };
      state.exclusionRules = { rules: {}, loading: false };
      state.attributionDetails = {
        loading: false,
        totalItems: 0,
        attributions: [],
        description: ''
      };
      state.activeRecon = { type: '' };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeasuresConditions.pending, (state) => {
        state.conditions = [];
        state.error = '';
        state.conditions = [];
      })
      .addCase(fetchMeasuresConditions.fulfilled, (state, action) => {
        state.conditions = action.payload.data;
      })
      .addCase(fetchMeasuresConditions.rejected, (state, action) => {
        state.error = (action as any).error?.message || '';
      })
      .addCase(fetchMeasuresPopulations.pending, (state) => {
        state.populations = [];
        state.error = '';
        state.populations = [];
      })
      .addCase(fetchMeasuresPopulations.fulfilled, (state, action) => {
        state.populations = action.payload.data;
      })
      .addCase(fetchMeasuresPopulations.rejected, (state, action) => {
        state.error = (action as any).error?.message || ''
      })
      .addCase(fetchQualityMeasuresDetails.pending, (state, action) => {
        const { size } = action.meta.arg;
        // You can derive type from size if needed
        const type = size === 5000 ? 'dropdown' : 'table';

        // Example usage:
        if (type === 'dropdown') {
          state.patients = [];
        } else {
          state.data = { measures: [] };
        }
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchQualityMeasuresDetails.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.type === 'dropdown') {
          state.patients = Array.isArray(action.payload.data?.measures)
            ? action.payload.data.measures
            : [];
          state.totalItems = action.payload.totalItems ?? 0;
        } else {
          state.data = action.payload.data || {};
          state.totalItems = action.payload.totalItems ?? 0;
        }
      })
      .addCase(fetchQualityMeasuresDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = (action as any).error?.message || '';
      })
      .addCase(fetchQualityMeasuresPatients.pending, (state) => {
        state.patients = [];
        state.error = '';
      }
      )
      .addCase(fetchQualityMeasuresPatients.fulfilled, (state, action) => {
        state.patients = action.payload.data;
      }
      )
      .addCase(fetchQualityMeasuresPatients.rejected, (state, action) => {
        state.error = (action as any).error?.message || '';
      })
      .addCase(fetchMeasuresCompliance.pending, (state) => {
        state.complianceDetails.loading = true;
        state.activeRecon = { type: '' };
        state.error = '';
      })
      .addCase(fetchMeasuresCompliance.fulfilled, (state, action) => {
        const complianceData = Array.isArray(action.payload?.data)
          ? action.payload.data
          : action.payload?.data?.data ?? [];
        state.complianceDetails.loading = false;
        state.complianceDetails.compliance = complianceData;
        state.complianceDetails.totalItems = action.payload?.totalItems ?? action.payload?.data?.totalItems;
        if (complianceData.length > 0) {
          const uniqueCaseIDs = uniqBy(complianceData, (item: { reconCaseSystemID: string }) => item.reconCaseSystemID);
          for (const caseId of uniqueCaseIDs) {
            const caseArray = filter(
              complianceData,
              (recon) => recon.reconCaseSystemID === caseId.reconCaseSystemID
            );
            const reconDataForCase: { reasonDescription?: string } & ReturnType<typeof getReconDataForCase> = getReconDataForCase(caseArray);
            state.activeRecon = { ...reconDataForCase, type: 'compliance' }
          };
        } else if (complianceData.length === 0 && state.activeRecon?.type === 'compliance') {
          state.activeRecon = {
            type: ''
          }
        }
      })
      .addCase(fetchMeasuresCompliance.rejected, (state, action) => {
        state.complianceDetails.loading = false;
        state.error = action.error.message || '';
      })
      .addCase(fetchMeasuresExclusion.pending, (state) => {
        state.exclusionDetails.loading = true;
        state.error = '';
        state.activeRecon = { type: '' };
        state.exclusionDetails.exclusions = [];
      })
      .addCase(fetchMeasuresExclusion.fulfilled, (state, action) => {
        const exclusionData = action.payload.data as Array<{ reconCaseSystemID: string; attributionExclusionDescription?: string }>;
        state.exclusionDetails.loading = false;
        state.exclusionDetails.exclusions = exclusionData;
        state.exclusionDetails.totalItems = action.payload.totalItems;
        if (exclusionData.length > 0) {
          let reconDataForCase: { reasonDescription?: string; type?: string } = {};
          const uniqueCaseIDs = uniqBy(exclusionData, 'reconCaseSystemID');
          for (const caseId of uniqueCaseIDs) {
            const caseArray = filter(
              exclusionData,
              (recon: { reconCaseSystemID: string }) => recon.reconCaseSystemID === caseId.reconCaseSystemID
            );
            reconDataForCase = getReconDataForCase(caseArray);
            // exclusion data also has attribution recons mixed in
            const attribution = caseArray[0].attributionExclusionDescription;
            if (attribution && attribution.length) {
              reconDataForCase.type = 'attribution';
              reconDataForCase.reasonDescription = caseArray[0].attributionExclusionDescription;
            } else {
              reconDataForCase.type = 'exclusion';
            }
          }
          state.activeRecon = { ...reconDataForCase }
        } else if (exclusionData.length === 0 && (state.activeRecon?.type === 'exclusion' || state.activeRecon?.type === 'attribution')) {
          state.activeRecon = {
            type: ''
          }
        }
      })
      .addCase(fetchMeasuresExclusion.rejected, (state, action) => {
        state.exclusionDetails.loading = false;
        state.error = action.error.message || '';
      })
      .addCase(fetchMeasuresComplianceRules.pending, (state) => {
        state.complianceRules.loading = true;
        state.error = '';
        state.complianceRules.rules = {};
      })
      .addCase(fetchMeasuresComplianceRules.fulfilled, (state, action) => {
        state.complianceRules.loading = false;
        state.complianceRules.rules = augmentRuleData(action.payload.data);
      }
      )
      .addCase(fetchMeasuresComplianceRules.rejected, (state, action) => {
        state.complianceRules.rules = {};
        state.complianceRules.loading = false;
        state.error = action.error.message || '';
      })
      .addCase(fetchMeasureExclusionRules.pending, (state) => {
        state.exclusionRules.loading = true;
        state.error = '';
        state.exclusionRules.rules = {};
      }
      )
      .addCase(fetchMeasureExclusionRules.fulfilled, (state, action) => {
        state.exclusionRules.loading = false;
        state.exclusionRules.rules = augmentRuleData(action.payload.data);
      }
      )
      .addCase(fetchMeasureExclusionRules.rejected, (state, action) => {
        state.exclusionRules.rules = {};
        state.exclusionRules.loading = false;
        state.error = action.error.message || '';
      }
      )
      .addCase(fetchMeasureAttribution.pending, (state) => {
        state.attributionDetails.loading = true;
        state.error = '';
        state.attributionDetails.attributions = [];
        state.attributionDetails.description = '';
      }
      )
      .addCase(fetchMeasureAttribution.fulfilled, (state, action) => {
        const service = action.payload?.data?.map((item: any) => {
          return {
            ...item,
            diagnosesData: computeDiagnosisData(item),
          };
        });
        state.attributionDetails.loading = false;
        state.attributionDetails.attributions = service;
        state.attributionDetails.totalItems = action.payload.totalItems;
        state.attributionDetails.description = service[0]?.attributionDescription;
      })
      .addCase(fetchMeasureAttribution.rejected, (state, action) => {
        state.attributionDetails.loading = false;
        state.error = action.error.message || '';
      })
      .addCase(fetchServiceDetails.pending, (state) => {
        state.serviceDetail = [];
      })
      .addCase(fetchServiceDetails.fulfilled, (state, action) => {
        const services: Array<{ date: string }> = action.payload.data || [];
        state.serviceDetail = services.map((detail: { date: string }) => {
          return {
            ...detail,
            date: formatDate(detail.date)
          };
        })
      })
      .addCase(fetchServiceDetails.rejected, (state, action) => {
        state.error = action.error.message || '';
      });
  },
});

export const { setReconModalData, setQualityDetails, clearQualityMeasuresInfo } = qualitySlice.actions;

export default qualitySlice.reducer;
